import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { toast } from "../lib/notices";
import { formatDateTime } from "../lib/datetime";
import { CheckCircle2, Loader2, X } from "lucide-react";
import VoicePreviewButton from "./VoicePreviewButton";

/*
 * Point a project at a cloned voice the account already owns.
 *
 * Enrolment (Settings -> "// YOUR VOICES") and *use* were two halves of a
 * feature that never met: the ledger was listable and the endpoint existed,
 * but nothing in the UI called it, so an enrolled voice could not narrate
 * anything. This is that missing half, and it is a component rather than a
 * page section because both the creation wizard and an existing project need
 * it — a voice is chosen once at creation and changed later just as often.
 *
 * POST use-voice, not PATCH: PATCH deliberately stopped accepting
 * `cloned_voice_id` so a caller could not point a project at a voice it does
 * not own, and the dedicated endpoint checks the ledger first (404, not 403,
 * for someone else's voice — a 403 would confirm the id exists).
 */

// A voice is identified by the (provider, id) pair, not the id alone — the
// same convention VoiceLibrary uses, because two providers may reuse an id.
const keyOf = (provider, voiceId) => `${provider}:${voiceId}`;

export default function ClonedVoicePicker({ projectId, value, onChange }) {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  // The (provider, id) key mid-request, or "clear" — a string rather than a
  // boolean so only the row actually being applied shows a spinner.
  const [busy, setBusy] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);
  const confirmBtn = useRef(null);

  const activeKey = value?.cloned_voice_id
    ? keyOf(value.cloned_voice_provider, value.cloned_voice_id)
    : "";

  const load = useCallback(async () => {
    try {
      const r = await api.get("/providers/my-voices");
      setVoices(Array.isArray(r.data?.voices) ? r.data.voices : []);
      setLoadError("");
    } catch (e) {
      setLoadError(errMsg(e, "Could not load your voices"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The confirm panel is announced by role="alert", but a keyboard user also
  // has to reach it — it renders below the button they just pressed.
  useEffect(() => { if (confirmClear) confirmBtn.current?.focus(); }, [confirmClear]);
  // A confirm panel left open once the project has no voice refers to nothing.
  useEffect(() => { if (!activeKey) setConfirmClear(false); }, [activeKey]);

  // Nothing here can be applied without a project row to apply it to; the
  // wizard renders this before step 1 has created one.
  if (!projectId) return null;

  const pick = async (v) => {
    const k = keyOf(v.provider, v.voice_id);
    if (k === activeKey || busy) return;
    setBusy(k);
    try {
      const r = await api.post(`/projects/${projectId}/use-voice`, null, {
        params: { provider: v.provider, voice_id: v.voice_id },
      });
      onChange?.({
        cloned_voice_id: r.data?.voice_id || v.voice_id,
        cloned_voice_provider: r.data?.provider || v.provider,
        // The response carries no name, so the label comes from the row the
        // user chose — which is the same string the server just stored.
        cloned_voice_name: v.name || v.voice_id,
        // Not part of the documented response today. Passed through when
        // present because the endpoint may repoint the project's TTS provider
        // at the endpoint that trained the voice, and a caller that re-sends
        // its own `tts_provider_id` afterwards (the wizard does, on generate)
        // would otherwise undo that silently.
        ...(r.data?.tts_provider_id ? { tts_provider_id: r.data.tts_provider_id } : {}),
      });
      toast.success(`Narrating with "${v.name || v.voice_id}"`);
    } catch (e) {
      toast.error(errMsg(e, "Could not use that voice"));
    } finally {
      setBusy("");
    }
  };

  const clear = async () => {
    setBusy("clear");
    try {
      await api.delete(`/projects/${projectId}/clone-voice`);
      onChange?.(null);
      setConfirmClear(false);
      toast.success("Cloned voice removed");
      // The route destroys the voiceprint, not just the link to it, so the
      // list below is now stale.
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not remove that voice"));
    } finally {
      setBusy("");
    }
  };

  const activeLabel = value?.cloned_voice_name || value?.cloned_voice_id || "";

  return (
    <section className="brutal-card mt-4" data-testid="cloned-voice-picker">
      <h3 className="marquee-tag mb-2">// USE A SAVED VOICE</h3>
      <p className="text-xs text-ink-muted leading-relaxed mb-4">
        Narrate this project with a cloned voice already on your account — no new recording
        needed. The voice belongs to you, not to one video, so the same one can read every
        project.
      </p>

      {loadError ? (
        <div className="text-sm text-state-danger" role="alert" data-testid="cloned-voices-error">
          {loadError}
        </div>
      ) : loading ? (
        <div className="text-sm text-ink-muted" data-testid="cloned-voices-loading">
          Loading your voices…
        </div>
      ) : voices.length === 0 ? (
        <div className="text-sm text-ink-muted" data-testid="cloned-voices-empty">
          <p className="mb-3">You have not enrolled a voice yet.</p>
          <Link to="/settings" className="brutal-btn brutal-btn-secondary inline-flex"
            data-testid="cloned-voice-enrol-link">
            Enrol a voice in Settings
          </Link>
        </div>
      ) : (
        // A button per row rather than a radio group: arrowing through radios
        // both moves and selects, so a keyboard user would fire one mutating
        // use-voice request per voice they passed over. The active row is
        // announced by the chip and by the disabled "In use" button instead.
        <ul className="grid gap-2 list-none p-0 m-0" data-testid="cloned-voices-list">
          {voices.map((v) => {
            const k = keyOf(v.provider, v.voice_id);
            const active = k === activeKey;
            const label = v.name || v.voice_id;
            return (
              <li key={k}
                className={`brutal-border p-3 flex items-start justify-between gap-3 ${active ? "bg-surface" : ""}`}
                data-testid={`cloned-voice-${v.voice_id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-sm break-all">{label}</span>
                    <span className="brutal-chip">{v.provider}</span>
                    {active && (
                      <span className="brutal-chip bg-state-success text-white border-rule"
                        data-testid={`cloned-voice-active-${v.voice_id}`}>
                        IN USE
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-muted mt-1">
                    Enrolled {formatDateTime(v.created_at)}
                  </div>
                  {(v.consent_subject || v.consent_granted_by) && (
                    <div className="text-xs text-ink-muted mt-1"
                      data-testid={`cloned-voice-consent-${v.voice_id}`}>
                      Consent: {v.consent_subject || "—"}, authorised by{" "}
                      {v.consent_granted_by || "—"}
                    </div>
                  )}
                </div>
                <VoicePreviewButton provider={v.provider} voiceId={v.voice_id}
                  label={v.name || v.voice_id} />
                <button type="button" onClick={() => pick(v)} disabled={active || !!busy}
                  className="brutal-btn brutal-btn-sm brutal-btn-secondary shrink-0"
                  data-testid={`use-voice-${v.voice_id}`}>
                  {busy === k
                    ? <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                    : <CheckCircle2 size={12} aria-hidden="true" />}
                  {active ? "In use" : busy === k ? "Applying…" : "Use this voice"}
                  {/* Keeps each button's accessible name unique in the list
                      without changing the words a voice-control user says. */}
                  <span className="sr-only"> — {label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {activeKey && (
        <div className="mt-4" data-testid="cloned-voice-active-panel">
          <button type="button" onClick={() => setConfirmClear((o) => !o)}
            disabled={!!busy}
            className="brutal-btn brutal-btn-secondary brutal-btn-sm"
            aria-expanded={confirmClear}
            data-testid="btn-clear-cloned-voice">
            <X size={12} aria-hidden="true" /> Stop using {activeLabel}
          </button>
          {confirmClear && (
            <div className="brutal-card brutal-card-flat bg-surface mt-3" role="alert"
              data-testid="cloned-voice-clear-confirm">
              <p className="text-xs leading-relaxed">
                Stop narrating with <b>{activeLabel}</b>? This does not only unlink it — the same
                route deletes the voiceprint from your account, so it disappears from Settings
                too, any other project using it reverts to a stock voice on its next render, and
                re-creating it needs a new recording. To keep the voice, pick a different one
                above instead.
              </p>
              <div className="flex gap-2 mt-3">
                <button type="button" onClick={clear} disabled={busy === "clear"}
                  ref={confirmBtn} className="brutal-btn"
                  data-testid="btn-confirm-clear-cloned-voice">
                  {busy === "clear"
                    ? <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                    : <X size={14} aria-hidden="true" />}
                  {busy === "clear" ? "Removing…" : "Remove and delete"}
                </button>
                <button type="button" onClick={() => setConfirmClear(false)}
                  disabled={busy === "clear"} className="brutal-btn brutal-btn-secondary"
                  data-testid="btn-cancel-clear-cloned-voice">
                  Keep it
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
